/**
 * WorkSurfacePage — Universal work surface for subject-centered work.
 *
 * Two modes:
 * 1. LANDSCAPE: Grid of subject cards with task counts (default)
 * 2. FOCUS: Single subject's tasks in TabulatorPrimitiveManager
 *
 * DRY: Works for ANY industry via subject_label from settings:
 * - Healthcare ALF: "Resident" → Ruth, Margaret, Harold
 * - Dental: "Patient" → Smith, Jones, Williams
 * - Auto Shop: "Vehicle" → Honda #42, Ford #17
 * - Bakery: "Order" → Wedding Cake #89, Bread Box
 *
 * Staff flow:
 * 1. Open WorkSurfacePage → see all subjects with pending work
 * 2. Click subject → see filtered tasks for that subject
 * 3. Complete tasks → counts update
 * 4. Back → return to landscape view
 *
 * DRR-256: Unified entry point for frontline staff.
 * DRR-055: Dumb frontend — labels from backend settings.
 */

import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Title,
  Group,
  Button,
  Tabs,
  Text,
  Stack,
  ThemeIcon,
  Paper,
} from '@mantine/core';
import { IconArrowLeft, IconChecklist, IconFileText, IconTimeline, IconUser } from '@tabler/icons-react';

import { LandscapeView, type SubjectWithCounts } from './LandscapeView';

// Types for the queries we'll need
interface Subject {
  id: string;
  name: string;
  actorType: string;
}

interface Task {
  id: string;
  status: string;
  priority: string;
  subjectActorId?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TabulatorComponentType = React.ComponentType<any>;

export interface WorkSurfacePageProps {
  readonly subjectLabel?: string;
  readonly subjects: Subject[];
  readonly subjectsLoading: boolean;
  readonly subjectsError?: string;
  readonly tasksBySubject: Map<string, Task[]>;
  readonly tasksLoading: boolean;
  readonly TabulatorComponent: TabulatorComponentType;
}

export function WorkSurfacePage({
  subjectLabel = 'Subject',
  subjects,
  subjectsLoading,
  subjectsError,
  tasksBySubject,
  tasksLoading,
  TabulatorComponent,
}: WorkSurfacePageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedSubjectId = searchParams.get('subject');

  // Find focused subject
  const focusedSubject = useMemo(() => {
    if (!focusedSubjectId) return null;
    return subjects.find((s) => s.id === focusedSubjectId) ?? null;
  }, [focusedSubjectId, subjects]);

  // Build subjects with counts
  const subjectsWithCounts = useMemo<SubjectWithCounts[]>(() => {
    return subjects.map((subject) => {
      const tasks = tasksBySubject.get(subject.id) ?? [];
      const total = tasks.length;
      const completed = tasks.filter((t) => t.status === 'COMPLETED').length;
      const critical = tasks.filter(
        (t) => t.priority === 'CRITICAL' && t.status !== 'COMPLETED'
      ).length;
      const overdue = tasks.filter((t) => {
        // Check if task is overdue (simplified - real impl would check dueAt)
        return t.status === 'PENDING' || t.status === 'IN_PROGRESS';
      }).length;

      return {
        id: subject.id,
        name: subject.name,
        taskCounts: { total, completed, critical, overdue: 0 }, // overdue needs dueAt check
      };
    });
  }, [subjects, tasksBySubject]);

  // Navigation handlers
  const handleSelectSubject = useCallback(
    (id: string) => {
      setSearchParams({ subject: id });
    },
    [setSearchParams]
  );

  const handleBack = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  // FOCUS VIEW: Show selected subject's tasks
  if (focusedSubject) {
    return (
      <Container size="xl" py="md">
        <Stack gap="md">
          {/* Header with back button */}
          <Group justify="space-between">
            <Group gap="md">
              <Button
                variant="subtle"
                leftSection={<IconArrowLeft size={16} />}
                onClick={handleBack}
              >
                All {subjectLabel}s
              </Button>
              <Group gap="xs">
                <ThemeIcon size="lg" radius="xl" color="blue" variant="light">
                  <IconUser size={18} />
                </ThemeIcon>
                <div>
                  <Text fw={500}>{focusedSubject.name}</Text>
                  <Text size="xs" c="dimmed">
                    {subjectLabel}
                  </Text>
                </div>
              </Group>
            </Group>
          </Group>

          {/* Primitive tabs */}
          <Tabs defaultValue="tasks">
            <Tabs.List>
              <Tabs.Tab value="tasks" leftSection={<IconChecklist size={16} />}>
                Tasks
              </Tabs.Tab>
              <Tabs.Tab value="documents" leftSection={<IconFileText size={16} />}>
                Documents
              </Tabs.Tab>
              <Tabs.Tab value="events" leftSection={<IconTimeline size={16} />}>
                Events
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="tasks" pt="md">
              <TabulatorComponent
                primitiveType="task"
                filterBy={[{ field: 'subjectActorId', value: focusedSubjectId! }]}
                groupBy="taskGroupRef"
                defaultViewMode="card"
              />
            </Tabs.Panel>

            <Tabs.Panel value="documents" pt="md">
              <TabulatorComponent
                primitiveType="document"
                filterBy={[{ field: 'subjectActorId', value: focusedSubjectId! }]}
                defaultViewMode="table"
              />
            </Tabs.Panel>

            <Tabs.Panel value="events" pt="md">
              <TabulatorComponent
                primitiveType="event"
                filterBy={[{ field: 'subjectActorId', value: focusedSubjectId! }]}
                defaultViewMode="table"
              />
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Container>
    );
  }

  // LANDSCAPE VIEW: Grid of all subjects with counts
  return (
    <LandscapeView
      subjects={subjectsWithCounts}
      loading={subjectsLoading || tasksLoading}
      error={subjectsError}
      subjectLabel={subjectLabel}
      onSelectSubject={handleSelectSubject}
    />
  );
}
